export const U2000FormStateEnumState = {
  READ: "READ",
  EDIT: "EDIT",
} as const;
export type U2000FormStateEnumState = (typeof U2000FormStateEnumState)[keyof typeof U2000FormStateEnumState];

export const U2000FormStateEnumEditMode = {
  NONE: "NONE",
  ADD: "ADD",
  EDIT: "EDIT",
} as const;
export type U2000FormStateEnumEditMode = (typeof U2000FormStateEnumEditMode)[keyof typeof U2000FormStateEnumEditMode];

export const U2000FormStateEnumAction = {
  SETDATA: "SETDATA",
  SETEDITMODE: "SETEDITMODE",
  SETTOUCHED: "SETTOUCHED",
  SETVALUE: "SETVALUE",
  SETERRORS: "SETERRORS",
  SETOTHERERRORS: "SETOTHERERRORS",
  SETSUBMITTED: "SETSUBMITTED",
  SAVE: "SAVE",
  CANCEL: "CANCEL",
  AFTERSAVE: "AFTERSAVE",
  SYNCFROMSERVER: "SYNCFROMSERVER",
  SETCUSTOMATTRIBUTES: "SETCUSTOMATTRIBUTES",
} as const;
export type U2000FormStateEnumAction = (typeof U2000FormStateEnumAction)[keyof typeof U2000FormStateEnumAction];

export interface U2000FormState<T = any> {
  state: (typeof U2000FormStateEnumState)[keyof typeof U2000FormStateEnumState];
  editMode: (typeof U2000FormStateEnumEditMode)[keyof typeof U2000FormStateEnumEditMode];
  current: T;
  old: T;
  touched: U2000FormStateTouch<T>;
  errors: U2000FormStateError<T>;
  otherErrors: U2000FormStateOtherError<T>;
  isSubmitted: boolean;
  customAttributes: Record<string, any>;
}

export interface U2000FormAction {
  type: string;
  [key: string]: any;
}

export type U2000FormStateTouch<T> = {
  [K in keyof T]?: boolean;
};

export type U2000FormStateError<T> = {
  [K in keyof T]?: string;
};

export type U2000FormStateOtherError<T> = {
  [K in keyof T]?: string | string[];
};

// Deep clone utility to avoid mutation issues
function deepClone<T>(obj: T): T {
  return obj === undefined ? obj : JSON.parse(JSON.stringify(obj));
}

// helper: set value by dotted path (returns new object)
function setByPath(obj: any, path: string | number, value: any) {
  const cloned = deepClone(obj ?? {});
  const parts = String(path).split(".");
  let cur = cloned;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (cur[p] === undefined || cur[p] === null || typeof cur[p] !== "object") {
      cur[p] = {};
    }
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return cloned;
}

function handleSetEditMode(formState: U2000FormState, action: U2000FormAction) {
  const { mode } = action.data;

  let state: (typeof U2000FormStateEnumState)[keyof typeof U2000FormStateEnumState] = U2000FormStateEnumState.READ;
  if (mode !== U2000FormStateEnumEditMode.NONE) {
    state = U2000FormStateEnumState.EDIT;
  }

  return {
    ...formState,
    old: deepClone(formState.current),
    state,
    editMode: mode,
  };
}

function handleSetValue(formState: U2000FormState, action: U2000FormAction) {
  const { id, value } = action.data;

  // support dotted paths like "user.username"
  if (String(id).includes(".")) {
    return {
      ...formState,
      current: setByPath(formState.current, id, value),
    };
  }

  return {
    ...formState,
    current: {
      ...formState.current,
      [id]: value,
    },
  };
}

function handleSetErrors(formState: U2000FormState, action: U2000FormAction) {
  const { errors } = action.data;

  return {
    ...formState,
    errors,
  };
}

function handleSetOtherErrors(formState: U2000FormState, action: U2000FormAction) {
  const { otherErrors } = action.data;

  return {
    ...formState,
    otherErrors,
  };
}

function handleSetSubmitted(formState: U2000FormState, action: U2000FormAction) {
  const { isSubmitted } = action.data;

  return {
    ...formState,
    isSubmitted,
  };
}

function handleSave(formState: U2000FormState, _action: U2000FormAction) {
  return {
    ...formState,
    old: formState.current,
    state: U2000FormStateEnumState.READ,
    editMode: U2000FormStateEnumEditMode.NONE,
    touched: {} as U2000FormState["touched"],
    errors: {} as U2000FormState["errors"],
    otherErrors: {} as U2000FormState["otherErrors"],
    isSubmitted: false,
  };
}

function handleAfterSave(formState: U2000FormState, action: U2000FormAction) {
  const { row } = action.data;

  return {
    ...formState,
    current: row,
    old: row,
    state: U2000FormStateEnumState.READ,
    editMode: U2000FormStateEnumEditMode.NONE,
    touched: {} as U2000FormState["touched"],
    errors: {} as U2000FormState["errors"],
    otherErrors: {} as U2000FormState["otherErrors"],
    isSubmitted: false,
  };
}

/** BL-0711 — refetch serveur sans quitter le mode édition ni marquer dirty (current + old alignés). */
function handleSyncFromServer(formState: U2000FormState, action: U2000FormAction) {
  const { row } = action.data;
  const cloned = deepClone(row);

  return {
    ...formState,
    current: cloned,
    old: cloned,
  };
}

function handleCancel(formState: U2000FormState, _action: U2000FormAction) {
  return {
    ...formState,
    current: formState.old,
    state: U2000FormStateEnumState.READ,
    editMode: U2000FormStateEnumEditMode.NONE,
    touched: {} as U2000FormState["touched"],
    errors: {} as U2000FormState["errors"],
    otherErrors: {} as U2000FormState["otherErrors"],
    isSubmitted: false,
  };
}

function handleSetTouched(formState: U2000FormState, action: U2000FormAction) {
  const { id } = action.data;
  return {
    ...formState,
    touched: {
      ...formState.touched,
      [id]: true,
    },
  };
}

function handleSetData(formState: U2000FormState, action: U2000FormAction) {
  const { row } = action.data;

  return {
    ...formState,
    current: row,
  };
}

function handleSetCustomAttributes(formState: U2000FormState, action: U2000FormAction) {
  const { customAttributes } = action.data;

  return {
    ...formState,
    customAttributes,
  };
}

const actionHandlers: Record<string, (form: U2000FormState, action: U2000FormAction) => U2000FormState> = {
  [U2000FormStateEnumAction.SETDATA]: handleSetData,
  [U2000FormStateEnumAction.SETEDITMODE]: handleSetEditMode,
  [U2000FormStateEnumAction.SETTOUCHED]: handleSetTouched,
  [U2000FormStateEnumAction.SETVALUE]: handleSetValue,
  [U2000FormStateEnumAction.SETERRORS]: handleSetErrors,
  [U2000FormStateEnumAction.SETOTHERERRORS]: handleSetOtherErrors,
  [U2000FormStateEnumAction.SETSUBMITTED]: handleSetSubmitted,
  [U2000FormStateEnumAction.SAVE]: handleSave,
  [U2000FormStateEnumAction.AFTERSAVE]: handleAfterSave,
  [U2000FormStateEnumAction.SYNCFROMSERVER]: handleSyncFromServer,
  [U2000FormStateEnumAction.CANCEL]: handleCancel,
  [U2000FormStateEnumAction.SETCUSTOMATTRIBUTES]: handleSetCustomAttributes,
};

export default function u2000FormState(form: U2000FormState, action: U2000FormAction): U2000FormState {
  const handler = actionHandlers[action.type];
  if (!handler) {
    throw new Error(`Unhandled action type: ${action.type}`);
  }
  return handler(form, action);
}
